import { PiCaretDown, PiCaretUp, PiCaretUpDown } from 'react-icons/pi';
import { Text } from 'src/modules/shared/components/atoms/text';
import type { ComponentVariant } from 'src/modules/shared/types/component.type';
import { tableHeaderText, tableVariantClasses } from './styles';

/** Direction supported by sortable table headers. */
export type TableSortDirection = 'asc' | 'desc';

/** Accessible label fragments used by sortable table headers. */
export interface TableSortableHeaderLabels {
  /** Prefix used before the column label in the accessible sort action. */
  sortBy: string;
  /** Direction label used when the next action sorts ascending. */
  ascending: string;
  /** Direction label used when the next action sorts descending. */
  descending: string;
}

/** Props for a shared sortable table header button. */
export interface TableSortableHeaderProps<TSortField extends string = string> {
  /** Visible column label. */
  label: string;
  /** Field sorted when this header is pressed. */
  field: TSortField;
  /** Field currently controlling table order. */
  activeField: TSortField;
  /** Direction currently controlling table order. */
  direction: TableSortDirection;
  /** Handles header-triggered sort changes. */
  onSort: (field: TSortField) => void;
  /** Visual variant matching the containing table. */
  variant?: ComponentVariant;
  /** Optional class applied to the button root. */
  className?: string;
  /** Optional class applied only to the visible label. */
  labelClassName?: string;
  /** Accessible label fragments for sort actions. */
  labels?: TableSortableHeaderLabels;
}

const DEFAULT_TABLE_SORTABLE_HEADER_LABELS: TableSortableHeaderLabels = {
  sortBy: 'Ordenar por',
  ascending: 'menor a mayor',
  descending: 'mayor a menor',
};

/**
 * Renders an accessible table header button that toggles a caller-owned sort field.
 *
 * @param props Header configuration and current sort state.
 * @returns Sortable table header button.
 */
export function TableSortableHeader<TSortField extends string = string>({
  label,
  field,
  activeField,
  direction,
  onSort,
  variant = 'secondary',
  className = '',
  labelClassName = '',
  labels = DEFAULT_TABLE_SORTABLE_HEADER_LABELS,
}: TableSortableHeaderProps<TSortField>) {
  const isActive = activeField === field;
  const SortIcon = isActive
    ? direction === 'asc'
      ? PiCaretUp
      : PiCaretDown
    : PiCaretUpDown;
  const nextDirectionLabel =
    isActive && direction === 'desc' ? labels.ascending : labels.descending;
  const variantClasses = tableVariantClasses[variant];

  return (
    <button
      type="button"
      className={`inline-flex items-center gap-1.5 rounded text-left leading-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${tableHeaderText} ${variantClasses.text} ${variantClasses.hoverText} ${className}`}
      onClick={() => onSort(field)}
      aria-label={`${labels.sortBy} ${label}: ${nextDirectionLabel}`}
      aria-pressed={isActive}
    >
      <Text
        as="span"
        size="small"
        weight="medium"
        color="inherit"
        className={labelClassName}
        disableAutoLink
      >
        {label}
      </Text>
      <SortIcon
        className={`h-3.5 w-3.5 flex-none ${
          isActive ? 'text-inherit' : 'text-neutral-400'
        }`}
        aria-hidden="true"
      />
    </button>
  );
}
